import { IQuotation } from "./quotationData.model";
import { IUser, IUserRef } from "./userData.model";
import type { EpcInvoiceReference } from "../utils/epcInvoiceReference";

export interface ICustomer {
  contactPersonName?: string;
  canViewQuotation?: boolean;
  leadId?: any;
  lastOrderDate?: string;
  _id?: string;
  epcName?: string;
  id?: number;
  name?: string;
  email?: string;
  mobileNumber?: string;
  billingAddress?: {
    line1: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
  };
  shippingAddress?: {
    line1: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
  };
  category?: string;
  goods?: [];
  quotation?: IQuotation[];
  gstin?: string;
  lastCalled?: number;
}

export interface ICompanyInfo {
  epcName: string;
  email: string;
  contactPersonName: string;
  mobileNumber: string;
  billingAddress: string;
  billingCity: string;
  billingState: string;
  billingPincode: string;
  quotationDate?: string;
  quotationCreatedBy?: string;
  quotationNumber?: string;
  contactNo?: string;
}
export interface IOrderCompanyInfo {
  epcName: string;
  email: string;
  mobileNumber: string;
  billingAddress: string;
  billingCity: string;
  billingState: string;
  billingPincode: string;
  quotationDate?: string;
  quotationCreatedBy?: string;
  quotationNumber?: string;
  contactNo?: string;
}

export interface IPaymentEntry {
  status?: string;
  customerMobileNumber: string;
  _id: string;
  amount: number;
  paymentProofUrl?: string;
  paymentAt: Date;
  docType?: "image" | "pdf";
  submittedVia?: string;
  isRoundOff?: boolean;
}

export interface IEpcMetaData {
  _id: string;
  fileProcessCharge: number;
  netMeterFile: number;
  dealerCommission: number;
}

export interface IEpcPayment extends IPaymentEntry {
  customer: string | IRegisteredUser;
  quotationNumber: string;
  mobileNumber: string;
  quote?: IEpcQuotation;
  submittedAt: any;
  createdAt: any;
  _id: string;
  customerMobileNumber: string;
  paymentPaid: number;
  paymentApproved: number;
  customerName: string;
  salesRepresentative: ISalesRepInfo | string;
  totalBillAmount: number;
  payments: IPaymentEntry[];
  paymentStatus: "pending" | "approved" | "rejected";
  roundOffAmount?: number;
  paidCalculated?: number;
  updatedAt?: Date;
  invoice: {
    generated: boolean;
    invoiceId: EpcInvoiceReference[];
    status: "NOT_GENERATED" | "GENERATED" | "CANCELLED" | "PARTIAL";
  };
}
export interface IKycDocument {
  _id?: string;
  name: string;
  link: string;
  isVerified?: boolean;
  uploadedAt: Date;
}

export interface ISalesRepInfo {
  _id?: string;
  name: string;
  email?: string;
  mobileNumber: string;
  address: string;
  gst?: string;
  panNumber?: string;
  bankAccountNumber?: string;
  bankAccountName?: string;
  ifsc?: string;
  companyName?: string;
  category: "Commission Dealer" | "Channel Partner";
  fileCharge?: number;
  portalCharge?: number;
  commissionValue?: number;
  commissionType?: "per_kw" | "per_order";
  cseId?: IUserRef;
}

export interface IproductRegistration {
  installationDate?: string | Date | null;
  panelVoltage: string;
  inverterVoltage: string;
  registrationNumber: string;
  totalSolarCapacity: string;
  panelMake: string;
  panelCapacity: string;
  numberOfModules: string;
  inverterMake: string;
  inverterCapacity: string;
  name?: string;
  address?: string;
  area?: string;
  consumerNumber?: string;
  panelBarcodes?: string[];
  inverterBarcodes?: string[];
  geoPhoto?: {
    name: string;
    link: string;
  };
  registeredAt?: Date;
}

export enum RegistrationStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  REJECTED = "rejected",
}

export interface IRegisteredUser {
  _id?: string;
  phoneNumber?: string;
  email?: string;
  customerName: string;
  mobileNumber: string;
  salesRepresentative?: string | ISalesRepInfo;
  cseID?: string | IUser;
  createdAt?: string;
  updatedAt?: string;
  frelitHandleNetMeterFileCost?: boolean;
  isPaymentDoneOnSalesRep?: boolean;
  totalBill?: number;
  kyc?: IKycDocument[];
  productRegistration?: IproductRegistration;
  registrationStatus?: RegistrationStatus;
  registrationRemark?: string;
}

export interface IEpcQuotation {
  quotationNumber: string;
  cseID: IUser;
  subsidyAmount: any;
  _id: string;
  plantSizeNumber?: number;
  phoneNumber: string;
  customerName: string;
  mobileNumber: string;
  salesRepresentative: ISalesRepInfo;
  brand: string;
  panelType: string;
  plantSize: string;
  plantCost: number;
  meterCost: number;
  totalCost: number;
  hsn: string;
  gst: number;
  createdAt: string;
  isConfirmed?: boolean;
  isActive?: boolean;
  confirmAt?: string;
  customCommission?: number;
  customCommissionType?: ICommissionType;
  excessAmount?: number;
  adjustmentAmount?: number;
  orderCreatedDate?: Date;
  adjustmentRemark?: string;
  otherInverter?: boolean;
  isConfirmedFromWeb?: boolean;
  discount?: number;
}
export interface IRegisteredUserWithDetails extends IRegisteredUser {
  payments?: IEpcPayment[];
  quote?: IEpcQuotation;
  currentPayment?: IEpcPayment;
  isLatestQuoteConfirmed?: boolean;
  latestQuoteId?: string;
  canCreateOrder: boolean;
}
export interface IOrderConfirmationData {
  name: string;
  email: string;
  mobileNumber: string;
  transportPrice: number;
  discountPrice: number;
  billingAddress: {
    line1: string;
    city: string;
    state: string;
    pincode: string;
  };
  shippingAddress: {
    line1: string;
    city: string;
    state: string;
    pincode: string;
  };
  gstin: string;
  sameAsBilling: boolean;
  orderNumber?: string;
}

export interface ICreateQuotationForm {
  customerName: string;
  mobileNumber: string;
  salesRepresentative?: string | undefined;
  cseID?: string | undefined;
  subsidyAmount: number;
  brand: string;
  panelType: string;
  plantSize: string;
  plantCost: number | undefined;
  meterCost: number;
  subsidyOption?: string;
  netMeterOption?: string;
  netMeterCost: number | undefined;
  brandOption?: string;
  customBrand?: string;
  discount?: number;
}
export interface StatusHistoryItem {
  _id: string;
  status: string;
  updatedAt: string;
  remark?: string;
  updatedBy?: {
    _id: string;
    username: string;
    name: string;
  };
}

export interface LoanDetailsItem {
  bankName: string;
  approvedDate: string;
  dateApplied: string;
  isJansmarath: boolean;
}

export interface QuoteStatusHistory {
  _id: string;
  quotationNumber: string;
  customerName: string;
  plantSize: string;
  totalCost: number;
  currentStatus: string;
  createdAt: string;
  statusHistory: StatusHistoryItem[];
  loanDetails: LoanDetailsItem;
}
export interface UploadState {
  feasibility: {
    file: File | null;
    isLoading: boolean;
  };
  eToken: {
    file: File | null;
    isLoading: boolean;
  };
}

export enum ICommissionType {
  per_kw = "per_kw",
  per_order = "per_order",
}

export interface BankDetails {
  bankAccountName?: string;
  bankAccountNumber?: string;
  ifsc?: string;
}

export interface ISalesRepAssigneeLog {
  oldValue: IUserRef | null;
  newValue: IUserRef | null;
  updatedBy: IUserRef | null;
  timestamp: string;
}

export interface ICSEWithSalesReps {
  cse: IUserRef;
  salesReps: ISalesRepInfo[];
}

export interface IAddRepaymentData {
  salesRepId: string;
  customerId: string;
  isChargesApplied: boolean;
  paymentAmount: number;
}
